import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { apiService } from '../services/api';
import './PublisherLayout.css';

interface PublisherLayoutProps {
  onLogout: () => void;
  publisherName: string;
  isAdminView?: boolean;
  basePath?: string;
  children?: React.ReactNode;
}

const PublisherLayout: React.FC<PublisherLayoutProps> = ({
  onLogout,
  publisherName,
  isAdminView = false,
  basePath = '/publisher',
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [resolvedPublisherName, setResolvedPublisherName] = useState(publisherName);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  const menuItems = useMemo(
    () => {
      const items = [
        { id: 'dashboard', label: 'Dashboard', icon: 'D', path: `${basePath}/dashboard` },
        { id: 'daily-report', label: 'Daily Report', icon: 'R', path: `${basePath}/daily-report` },
        { id: 'event-report', label: 'Event Report', icon: 'E', path: `${basePath}/event-report` },
        { id: 'install-report', label: 'Install Report', icon: 'I', path: `${basePath}/install-report` },
        { id: 'offers', label: 'Offers', icon: 'O', path: `${basePath}/offers` },
        { id: 'top-offers', label: 'Top Offers', icon: 'T', path: `${basePath}/top-offers` },
        { id: 'settings', label: 'Settings', icon: 'S', path: `${basePath}/settings` },
      ];

      return isAdminView
        ? items.filter((item) => !['top-offers', 'settings'].includes(item.id))
        : items;
    },
    [basePath, isAdminView]
  );

  const isActive = (path: string) => {
    if (path.endsWith('/dashboard') && location.pathname === basePath) {
      return true;
    }
    return location.pathname === path;
  };
  const displayName = resolvedPublisherName || publisherName || 'Publisher';
  const initials = displayName
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'P';

  const handleNavigate = (path: string) => {
    setIsUserMenuOpen(false);
    navigate(path, { state: location.state });
  };

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    onLogout();
  };

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadPublisherProfile = async () => {
      if (isAdminView) {
        return;
      }

      try {
        const response = await apiService.getPublisherMe();
        if (isMounted && response?.publisher?.name) {
          setResolvedPublisherName(response.publisher.name);
        }
      } catch {
        // Keep the provided fallback name if the profile request fails.
      }
    };

    void loadPublisherProfile();

    return () => {
      isMounted = false;
    };
  }, [isAdminView]);

  return (
    <div className={`publisher-layout ${isCollapsed ? 'publisher-layout-collapsed' : ''}`}>
      <aside className="publisher-sidebar">
        <div className="publisher-sidebar-top">
          <button
            type="button"
            className="publisher-collapse-btn"
            onClick={() => setIsCollapsed((current) => !current)}
            aria-label={isCollapsed ? 'Expand publisher sidebar' : 'Collapse publisher sidebar'}
            title={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {isCollapsed ? '>' : '<'}
          </button>

          <div className="sidebar-logo">
            <img src="/site-logo.jpeg" alt="AdBizz Media" className="sidebar-logo-image" />
            {!isCollapsed && <p>Publisher Portal</p>}
          </div>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => handleNavigate(item.path)}
              title={isCollapsed ? item.label : undefined}
            >
              <span className="nav-icon">{item.icon}</span>
              {!isCollapsed && <span className="nav-label">{item.label}</span>}
            </button>
          ))}
        </nav>
      </aside>

      <main className="publisher-main">
        <header className="publisher-topbar">
          <div className="publisher-topbar-spacer" />
          <div className="publisher-user-menu" ref={userMenuRef}>
            <button
              type="button"
              className="publisher-user-trigger"
              onClick={() => setIsUserMenuOpen((current) => !current)}
              aria-haspopup="menu"
              aria-expanded={isUserMenuOpen}
            >
              <span className="publisher-avatar">{initials}</span>
              <span className="publisher-user-name">{displayName}</span>
              <span className="publisher-user-caret">v</span>
            </button>

            {isUserMenuOpen && (
              <div className="publisher-user-dropdown" role="menu">
                {!isAdminView && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleNavigate(`${basePath}/settings`)}
                  >
                    Settings
                  </button>
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </header>

        <section className="publisher-main-content">
          {isAdminView && children ? children : <Outlet />}
        </section>
      </main>
    </div>
  );
};

export default PublisherLayout;
